import React, { createContext, useContext, useState } from 'react';
import { Role } from '../types/roles';
import { getRoles, createRole, updateRole, deleteRole as deleteApi } from '../api/roleApi';
import { handleWithAlert } from '../utils/handleWithAlert';

interface RoleContextProps {
  roles: Role[];
  addRole: (role: Role) => void;
  updateRole: (role: Role) => void;
  deleteRole: (id: string) => void;
  listRoles: () => Promise<void>;
}

const RoleContext = createContext<RoleContextProps | undefined>(undefined);

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [roles, setRoles] = useState<Role[]>([]);

  const addRole = async (role: Role) => {
    const created = await handleWithAlert(() => createRole(role), {
      successMessage: 'Role created successfully!',
      errorMessage: 'Failed to create role',
      showSuccess: true,
    });
    if (created) listRoles();
  };

  const updateRoleFn = async (updated: Role) => {
    const updatedRole = await handleWithAlert(() => updateRole(updated.id!, updated), {
      successMessage: 'Role updated successfully!',
      errorMessage: 'Failed to update role',
      showSuccess: true,
    });
    if (updatedRole) { listRoles(); }
    else {
      setRoles(prev => prev.map(r => (r.id === updated.id ? { ...r, ...updated } : r)));
    }
  };

  const deleteRole = async (id: string) => {
    const deleted = await handleWithAlert(() => deleteApi(id), {
      successMessage: 'Role deleted successfully!',
      errorMessage: 'Failed to delete role',
      showSuccess: true,
    });
    if (deleted !== null) {
      setRoles(prev => prev.filter(r => r.id !== id));
    }
  };

  const listRoles = async () => {
    const list = await handleWithAlert(() => getRoles());
    if (list) setRoles(list);
  };

  return (
    <RoleContext.Provider value={{ roles, addRole, updateRole: updateRoleFn, deleteRole, listRoles }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) throw new Error('useRole must be inside RoleProvider');
  return context;
};
